import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { BotIcon, Maximize2Icon, Minimize2Icon, SparklesIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AgentConversation } from '@/components/agent/agent-conversation';
import type { SharedAgentRecord } from './shared-chat-context';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { describeAiContext, useAiAssistantStore } from '@/components/focus-queue/ai-context';
import { contextRecordIds } from '@/components/focus-queue/ai-context-records';
import { agentAreaGuide, areaGuidePresentation } from './agent-area-guide';

/** Builds the first message for a ⌘J/Ask AI request so the assistant knows what "this" is. */
export function contextualPrompt(prompt: string | null, context: string): string {
  if (!prompt) return '';
  return context ? `${prompt}\n\n(${context})` : prompt;
}

export function boundedAgentWidth(width: number, viewport: number) {
  return Math.round(Math.max(Math.min(560, viewport * 0.96), Math.min(width, viewport * 0.96)));
}

export function AgentPanel({ record }: { record?: SharedAgentRecord } = {}) {
  const location = useLocation();
  const store = useAiAssistantStore();
  const [localOpen, setLocalOpen] = React.useState(false);
  const open = store ? store.open : localOpen;
  const setOpen = store ? store.setOpen : setLocalOpen;
  const [initial, setInitial] = React.useState<{ key: number; message: string }>({ key: 0, message: '' });
  const contextText = describeAiContext(store?.context ?? null);
  const contextRecord = store?.context?.record ?? null;
  const recordIds = React.useMemo(() => contextRecordIds(contextRecord?.kind, contextRecord?.id), [contextRecord?.kind, contextRecord?.id]);
  const suggestions = store?.context?.suggestions ?? [];
  // What the Agent can help with where it was opened, e.g. an account page.
  const areaGuide = React.useMemo(() => agentAreaGuide(location.pathname, store?.context ?? null), [location.pathname, store?.context]);
  const presentation = React.useMemo(() => areaGuide ? areaGuidePresentation(areaGuide) : undefined, [areaGuide]);
  const starterContext = contextText || (areaGuide ? `Page: ${areaGuide.area}` : '');
  const consumePrompt = store?.consumePrompt;
  // Apply a pending "Ask AI" prompt (with the current record/queue context) each time the panel opens.
  React.useEffect(() => {
    if (!open || !consumePrompt) return;
    const prompt = consumePrompt();
    if (prompt) setInitial(value => ({ key: value.key + 1, message: contextualPrompt(prompt, contextText) }));
  }, [open, consumePrompt, contextText]);
  // ⌘J / Ctrl+J toggles the assistant slide-over on every staff page.
  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.key.toLowerCase() !== 'j') return;
      event.preventDefault();
      if (open && leaveGuardRef.current?.() === false) return;
      setOpen(!open);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, setOpen]);
  const [expanded, setExpanded] = React.useState(false);
  const [width, setWidth] = React.useState<number>();
  const [researchMode, setResearchMode] = React.useState<'internal' | 'public_research'>('internal');
  const leaveGuard = React.useRef<(() => boolean) | null>(null);
  const leaveGuardRef = leaveGuard;
  const registerGuard = React.useCallback((guard: (() => boolean) | null) => { leaveGuard.current = guard; }, []);
  const drag = React.useRef<{ x: number; width: number } | null>(null);
  React.useEffect(() => {
    const resize = () => setWidth(value => value === undefined ? value : boundedAgentWidth(value, window.innerWidth));
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  if (location.pathname === '/agent') return null;

  return (
    <Sheet open={open} onOpenChange={next => { if (next || leaveGuard.current?.() !== false) setOpen(next); }}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="shrink-0" aria-label="Open Agent" title="Ask AI (⌘J)">
          <SparklesIcon className="size-4" /><span className="hidden sm:inline">Ask AI</span>
          <kbd className="text-muted-foreground ml-1 hidden rounded border px-1 text-[10px] lg:inline">⌘J</kbd>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" showCloseButton={!expanded} style={width === undefined ? undefined : { '--agent-width': `${width}px` } as React.CSSProperties}
        data-expanded={expanded}
        className={cn("flex w-full max-w-none flex-col gap-0 overflow-hidden p-0", expanded
          ? "h-dvh border-l-0 sm:w-full sm:max-w-none"
          : "sm:w-[var(--agent-width,min(56rem,92vw))] sm:max-w-[96vw]")}>
        {!expanded && <div role="separator" aria-label="Resize Agent panel" aria-orientation="vertical" tabIndex={0}
          aria-valuemin={Math.round(Math.min(560, window.innerWidth * 0.96))}
          aria-valuemax={Math.round(window.innerWidth * 0.96)}
          aria-valuenow={width ?? Math.round(Math.min(896, window.innerWidth * 0.92))}
          className="absolute inset-y-0 left-0 z-20 hidden w-2 cursor-col-resize touch-none focus-visible:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:block"
          onPointerDown={event => { if (event.button !== 0) return; drag.current = { x: event.clientX, width: event.currentTarget.parentElement?.getBoundingClientRect().width ?? 896 }; event.currentTarget.setPointerCapture(event.pointerId); }}
          onPointerMove={event => { if (drag.current) setWidth(boundedAgentWidth(drag.current.width + drag.current.x - event.clientX, window.innerWidth)); }}
          onPointerUp={event => { drag.current = null; if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); }}
          onPointerCancel={() => { drag.current = null; }}
          onLostPointerCapture={() => { drag.current = null; }}
          onKeyDown={event => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const current = width ?? Math.min(896, window.innerWidth * 0.92);
            setWidth(boundedAgentWidth(event.key === 'Home' ? 560 : event.key === 'End' ? window.innerWidth : current + (event.key === 'ArrowLeft' ? 32 : -32), window.innerWidth));
          }} />}
        <SheetHeader className={cn("min-h-14 shrink-0 flex-row items-center space-y-0 border-b px-3 py-2 pr-24 text-left", expanded && "sr-only")}>
          <SheetTitle className="flex items-center gap-2 text-sm"><BotIcon className="size-4" /> Agent</SheetTitle>
          {!record && <div role="group" aria-label="Assistant mode" className="ml-3 flex gap-1">
            {(['internal', 'public_research'] as const).map(value => <Button key={value} type="button" size="sm" variant={researchMode === value ? 'secondary' : 'ghost'} className="h-7 px-2 text-xs"
              aria-pressed={researchMode === value} onClick={() => { if (researchMode !== value && leaveGuard.current?.() !== false) setResearchMode(value); }}>
              {value === 'internal' ? 'Assistant' : 'Research'}
            </Button>)}
          </div>}
          <SheetDescription className="sr-only">Work in an explicitly activated scope. Changes stay review-only until approved.</SheetDescription>
        </SheetHeader>
        <Button type="button" variant="ghost" size="icon" className={cn("absolute top-2 z-20 size-9 shrink-0", expanded ? "right-2 bg-background/90 shadow-sm" : "right-12")}
          aria-label={expanded ? "Restore Agent panel" : "Expand Agent to full window"}
          title={expanded ? "Restore Agent panel" : "Expand Agent to full window"}
          aria-pressed={expanded} onClick={() => { drag.current = null; setExpanded(value => !value); }}>
          {expanded ? <Minimize2Icon className="size-4" /> : <Maximize2Icon className="size-4" />}
        </Button>
        {contextText && !expanded ? (
          <div className="bg-muted/50 flex flex-wrap items-center gap-1.5 border-b px-3 py-2 text-xs" aria-label="Assistant context">
            <span className="text-muted-foreground">Context:</span><span className="min-w-0 truncate font-medium">{contextText}</span>
            {suggestions.slice(0, 3).map(suggestion => (
              <Button key={suggestion} type="button" size="sm" variant="outline" className="h-6 px-2 text-xs"
                onClick={() => setInitial(value => ({ key: value.key + 1, message: contextualPrompt(suggestion, contextText) }))}>
                {suggestion}
              </Button>
            ))}
          </div>
        ) : null}
        <div className="flex min-h-0 flex-1 flex-col"><AgentConversation key={initial.key} initialMessage={initial.message || undefined} presentation={record ? undefined : presentation} starterContext={starterContext || undefined} record={record} recordIds={recordIds} researchMode={record ? 'internal' : researchMode} fillHeight chatOnly={expanded} onLeaveGuardChange={registerGuard} /></div>
      </SheetContent>
    </Sheet>
  );
}
